"use client";
import { Divider } from "@/components/Divider";
import {
  PlayStoreIcon,
  AppStoreIcon,
  FacebookIcon,
  InstagramIcon,
  YoutubeIcon,
} from "@/components/Icons";
import { Column, Row } from "@/components/Stack";
import { Text } from "@/components/Text";
import {
  appStoreLink,
  DialectsEnum,
  langDetails,
  playStoreLink,
  STAGE_CONTACT_NUMBER,
  SourceWidgetsEnum,
  MediaNameEnum,
  MAX_FOOTER_LINKS_COUNT,
  CookiesEnum,
  cookiesValues,
  AppRoutesEnum,
} from "@/constants/constants";
import { DIALECT_CONFIGS, getEnabledDialects } from "@/config/dialects.config";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import Link from "next/link";
import { isTvEnv } from "@/utils/common.helpers";
import { LinksData, Top20ContentDataBodyInterface } from "@/types/home.types";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import { useEffect, useState } from "react";
import { Dot } from "lucide-react";
import { API_ROUTES } from "@/apiSdk/apiRoutes";
import { usePathname, useRouter } from "next/navigation";
import { useUserContext } from "@/context/UserProvider";
import { setCookieViaAction } from "@/actions/user.actions";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { EventNamesEnum } from "@/constants/constants";

interface FooterSectionProps {
  title: string;
  links: LinksData[];
  isAddress?: boolean;
}

export const HomeFooter = ({
  footerPopularLinks,
  showFaqLink = true,
  showContactUsLink = true,
  transparentBg = false,
}: {
  footerPopularLinks?: { [key in DialectsEnum]?: LinksData[] };
  showFaqLink?: boolean;
  showContactUsLink?: boolean;
  transparentBg?: boolean;
}) => {
  const { t, currentLanguage } = useClientTranslations();
  const pathname = usePathname();

  const pathParts = pathname.split("/").filter(Boolean);
  const dialectFromPath = pathParts.find((part) =>
    Object.values(DialectsEnum).includes(part as DialectsEnum),
  ) as DialectsEnum | undefined;

  // Use dialect from pathname, or default to HARYANVI
  const dialectUrlValue: string = dialectFromPath || DialectsEnum.HARYANVI;

  // Dynamic state management - single object for all dialect links
  // Replaces 4 hardcoded state variables (haryanviLinks, rajastaniLinks, etc.)
  const [dialectLinks, setDialectLinks] = useState<
    Record<DialectsEnum, LinksData[]>
  >(() => {
    // Initialize with empty arrays for all dialects
    const initialState = {} as Record<DialectsEnum, LinksData[]>;
    Object.values(DialectsEnum).forEach((dialect) => {
      initialState[dialect] = [];
    });
    return initialState;
  });

  const linkPrefix = `/${currentLanguage}`;

  let socialLinks: {
    href: string;
    mediaName: MediaNameEnum;
  }[] = [
    {
      mediaName: MediaNameEnum.INSTAGRAM,
      href: "https://www.instagram.com/stagedotin?igsh=MTgwcjl5ZG9uaTk1dA==",
    },
    {
      mediaName: MediaNameEnum.FACEBOOK,
      href: "https://www.facebook.com/STAGEdotin",
    },
    {
      mediaName: MediaNameEnum.YOUTUBE,
      href: "https://www.youtube.com/@STAGEdotin",
    },
  ];

  // Dynamic footer links generation - uses enabled dialects from config
  // Replaces 4 hardcoded dialect columns with dynamic generation
  const dialectFooterColumns: FooterSectionProps[] = Object.values(
    DIALECT_CONFIGS,
  )
    .filter((config) => config.enabled) // Only show enabled dialects
    .map((config, index) => ({
      title: t(`home_page.footer.col${index + 1}Title`),
      links:
        footerPopularLinks?.[config.enum] || dialectLinks[config.enum] || [],
    }));

  let footerLinks: FooterSectionProps[] = [
    ...dialectFooterColumns,
    {
      title: t(`home_page.footer.col${dialectFooterColumns.length + 1}Title`),
      links: [
        { href: linkPrefix + "/about-us", text: t("setting_page.about_us") },
        {
          href: "https://stage.zohorecruit.in/jobs/Careers",
          text: t("setting_page.career"),
          rel: "nofollow",
        },
        {
          href: "https://docs.google.com/document/d/1o3AGoYqGzPEIQTMslTlKFGn7-x0wcNCVoZpwDT9Hjcg/edit?usp=sharing",
          text: t("home_page.footer.compliance_report"),
          rel: "nofollow",
          target: "_blank",
        },
        ...(showFaqLink
          ? [
              {
                href: linkPrefix + "/faq",
                text: t("common.faq"),
              },
            ]
          : []),
        ...(showContactUsLink
          ? [
              {
                href:
                  linkPrefix +
                  `/${dialectUrlValue}${AppRoutesEnum.SETTINGS}${AppRoutesEnum.CONTACT_US}`,
                text: t("setting_page.contact_us"),
              },
            ]
          : []),
        {
          href: linkPrefix + "/refund-policy",
          text: t("auth_screen.refund_policy"),
        },
      ],
    },
    {
      title: t(`home_page.footer.col${dialectFooterColumns.length + 2}Title`),
      isAddress: true,
      links: [
        {
          text: STAGE_CONTACT_NUMBER,
          href: `tel:${STAGE_CONTACT_NUMBER}`,
          rel: "",
          target: "",
        },
      ],
    },
  ];

  useEffect(() => {
    let isMounted = true;

    // If parent already provided the data, don't fetch
    if (footerPopularLinks) {
      return;
    }

    const fetchPopularContent = async (dialect: DialectsEnum) => {
      const popularContent =
        await apiSdkInstance.home.getDynamicWidgetData<Top20ContentDataBodyInterface>(
          {
            dialect,
            lang: currentLanguage,
            endpoint: API_ROUTES.home.top20Content,
            apiResKey: SourceWidgetsEnum.TOP20CONTENT,
          },
        );
      if ("error" in popularContent) {
        return null;
      }
      return popularContent;
    };

    const fetchAllPopularContent = async () => {
      const enabledDialects = getEnabledDialects().map((c) => c.enum);
      for (const dialect of enabledDialects) {
        if (!isMounted) break; // Exit early if component unmounted

        const popularContent = await fetchPopularContent(dialect);
        if (
          Array.isArray(popularContent) &&
          popularContent &&
          popularContent.length > MAX_FOOTER_LINKS_COUNT &&
          "error" in popularContent[0] &&
          popularContent[0].error
        ) {
          continue;
        }
        if (!isMounted) break; // Check again after async call

        if (popularContent) {
          const links = popularContent
            .slice(0, MAX_FOOTER_LINKS_COUNT)
            .map((content) => ({
              text: "title" in content ? content.title : "",
              href: "contentUrl" in content ? content.contentUrl : "",
            }));

          // Dynamic setter - updates single dialectLinks object
          // Replaces 4 hardcoded if-else statements
          setDialectLinks((prev) => ({
            ...prev,
            [dialect]: links,
          }));
        }
      }
    };

    fetchAllPopularContent();

    return () => {
      isMounted = false;
    };
  }, [currentLanguage, footerPopularLinks]);

  if (isTvEnv()) return null;

  return (
    <footer
      className={`mt-6 px-4 py-10 md:px-12 ${
        transparentBg
          ? "border-t border-white/[0.06] bg-black/40 backdrop-blur-xl"
          : "bg-stg-grey-0"
      }`}
    >
      <div
        className="mb-10 grid gap-8 md:gap-16"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        }}
      >
        {footerLinks?.map(({ title, links, isAddress }, index) => {
          if (links.length === 0) {
            return null;
          }
          return isAddress ? (
            <address className="not-italic" key={index}>
              <FooterSection title={title} links={links} />
            </address>
          ) : (
            <FooterSection title={title} links={links} key={index} />
          );
        })}
      </div>
      <Divider />
      <Row align="center" justify="spaceBetween" className="gap-5">
        <Column>
          <Row align="center" gapMd={3}>
            <Text weight="bold" sizeMd={5} tag="h2">
              {t("home_page.footer.website_language")}
            </Text>
            <LanguageSelect currentLanguage={currentLanguage} />
          </Row>
        </Column>
        <Column>
          <Row align="center" gapMd={3}>
            <Text weight="bold" sizeMd={5} tag="h2">
              {t("home_page.footer.connect_with_us")}
            </Text>
            <Row gap={1}>
              {socialLinks.map(({ href, mediaName }) => (
                <SocialLink key={href} href={href} mediaName={mediaName} />
              ))}
            </Row>
            <Row align="center" className="" gap={1}>
              <Link
                href={appStoreLink}
                target="_blank"
                title="Download Stage App from App Store"
                rel="nofollow"
                className="rounded-lg border border-stg-grey-3 hover:border-stg-grey-4"
              >
                <AppStoreIcon />
              </Link>
              <Link
                href={playStoreLink}
                target="_blank"
                title="Download Stage App from Google Play Store"
                rel="nofollow"
                className="rounded-lg border border-stg-grey-3 hover:border-stg-grey-4"
              >
                <PlayStoreIcon />
              </Link>
            </Row>
          </Row>
        </Column>
      </Row>
      <Divider />
      <Row align="center" justify="start" className="mt-5 items-center">
        <Text sizeMd={3} classNames="!leading-7 !text-stg-grey-3">
          © {new Date().getFullYear()} STAGE
        </Text>
        <Dot className="text-stg-grey-4" />
        <Link
          href={linkPrefix + "/terms"}
          target="_blank"
          className="text-sm leading-7 text-stg-grey-3 hover:text-stg-grey-4"
        >
          {t("setting_page.term_of_use")}
        </Link>
        <Dot className="text-stg-grey-4" />
        <Link
          href={linkPrefix + "/privacy"}
          target="_blank"
          className="text-sm leading-7 text-stg-grey-3 hover:text-stg-grey-4"
        >
          {t("setting_page.privacy_policy")}
        </Link>
      </Row>
    </footer>
  );
};

const SocialLink = ({
  href,
  mediaName,
}: {
  href: string;
  mediaName: MediaNameEnum;
}) => {
  const title = `Stage ${mediaName} handle`;
  return (
    <Link
      href={href}
      target="_blank"
      title={title}
      rel="nofollow"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-stg-grey-3 hover:border-stg-grey-4"
    >
      {(() => {
        switch (mediaName) {
          case MediaNameEnum.INSTAGRAM:
            return <InstagramIcon />;
          case MediaNameEnum.FACEBOOK:
            return <FacebookIcon />;
          case MediaNameEnum.YOUTUBE:
            return <YoutubeIcon />;
          default:
            return null;
        }
      })()}
    </Link>
  );
};

const FooterSection = ({ title, links }: FooterSectionProps) => {
  const { trackEvent } = useTrackingAnalytics();
  return (
    <Column gap={2} className="w-52">
      <Text weight="bold" sizeMd={5} tag="h2">
        {title}
      </Text>
      <Column>
        {links.map((link, index) => (
          <Link
            key={index}
            href={link.href}
            rel={link?.rel}
            target={link?.target}
            onClick={async () => {
              if (link.href.startsWith("tel:") && trackEvent) {
                await trackEvent({
                  eventName: EventNamesEnum.CUSTOMER_SUPPORT_CLICKED,
                  properties: {
                    source: "footer",
                    mode: "call",
                  },
                });
              }
            }}
          >
            <p className="text-sm leading-7 text-stg-grey-5 hover:text-white hover:underline">
              {link.text}
            </p>
          </Link>
        ))}
      </Column>
    </Column>
  );
};

const LanguageSelect = ({ currentLanguage }: { currentLanguage: string }) => {
  const router = useRouter();
  const { updateUserLang } = useUserContext();
  const pathname = usePathname();

  return (
    <>
      {/* 
       The native <select> element's arrow icon is rendered by the browser and cannot be styled directly with Tailwind or CSS. 
        Padding on the right side of the select may not work as expected because the arrow icon overlays the content area, 
        and browsers often ignore or override right padding to ensure the arrow is visible. 
        To fully control the appearance and padding, you can wrap the select in a relative container and add a custom arrow icon absolutely positioned on the right.
      */}
      <div className="relative inline-block">
        <select
          className="language-select text-stg-grey-7 cursor-pointer appearance-none rounded border border-stg-grey-4 bg-transparent p-2 pr-6 text-xs hover:bg-stg-grey-2 focus:outline-none"
          value={currentLanguage}
          onChange={(e) => {
            const selectedLang = e.target.value;
            updateUserLang(selectedLang);
            if (pathname.includes(currentLanguage)) {
              const newPath = pathname.replace(currentLanguage, selectedLang);
              router.push(newPath);
            } else {
              setCookieViaAction({
                key: cookiesValues[CookiesEnum.Lang],
                value: selectedLang,
              });
            }
          }}
          aria-label="Select website language"
        >
          {Object.keys(langDetails).map((langKey) => (
            <option value={langKey} key={langKey}>
              {langDetails[langKey as keyof typeof langDetails].label}
            </option>
          ))}
        </select>
        {/* Custom arrow icon, absolutely positioned */}
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stg-grey-4">
          <svg width="16" height="16" fill="none" viewBox="0 0 20 20">
            <path
              d="M6 8l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </>
  );
};
